import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { adminApi, MostUsedToolsReport } from '../services/api';
import PageHelp from '../components/PageHelp';
import { PAGE_HELP } from '../components/page-help-content';
import { getCurrentPeriod, getRecentPeriods, formatPeriodLabel } from '../utils/period';
import { formatPercent } from '../utils/format';

// Cap the chart to the busiest tools so a long tail of one-off tools doesn't
// squash the bars; the full list is always in the table below.
const MAX_CHART_BARS = 12;

const MostUsedToolsPage: React.FC = () => {
  const [period, setPeriod] = useState<string>(getCurrentPeriod());
  const [report, setReport] = useState<MostUsedToolsReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const periods = useMemo(() => getRecentPeriods(), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminApi
      .getMostUsedTools({ period })
      .then((response) => {
        if (cancelled) return;
        if (response.success) {
          setReport(response.data);
        } else {
          setError('Failed to load most-used tools.');
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load most-used tools.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  const summary = report?.summary;

  const cards: { key: string; caption: string; value: string }[] = [
    { key: 'runs', caption: 'Total runs', value: (summary?.totalRuns ?? 0).toLocaleString() },
    { key: 'tools', caption: 'Distinct tools used', value: (summary?.distinctTools ?? 0).toLocaleString() },
    { key: 'top', caption: 'Most-used tool', value: summary?.topToolDisplayName || '—' },
  ];

  // Recharts horizontal bars: layout="vertical" puts the category (tool name) on
  // the Y axis and the run count on the X axis.
  const chartData = useMemo(
    () =>
      (report?.tools ?? [])
        .filter((t) => t.runs > 0)
        .slice(0, MAX_CHART_BARS)
        .map((t) => ({ name: t.displayName, runs: t.runs })),
    [report],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Most-Used Tools
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Which tools brokers actually run, for the selected month — so the team can see what's
            valued and where to focus. Usage is metered in runs (~200k tokens each); each run is
            counted once for the user who ran it.
          </Typography>
        </Box>
        <TextField
          select
          label="Period"
          size="small"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          sx={{ minWidth: 200 }}
          inputProps={{ 'data-testid': 'period-select' }}
        >
          {periods.map((p) => (
            <MenuItem key={p} value={p}>
              {formatPeriodLabel(p)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <PageHelp {...PAGE_HELP.mostUsedTools} />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && report && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 3 }}
            data-testid="summary-cards"
          >
            {cards.map((card) => (
              <Card key={card.key} sx={{ flex: 1 }} data-testid={`card-${card.key}`}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    {card.caption}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, mt: 1 }}
                    data-testid={`card-${card.key}-value`}
                  >
                    {card.value}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {chartData.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }} data-testid="tool-chart">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Runs by tool {chartData.length < (report.tools.filter((t) => t.runs > 0).length) && `(top ${MAX_CHART_BARS})`}
              </Typography>
              <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 34)}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 24, right: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="runs" name="Runs" fill="#3182ce" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          <Paper variant="outlined">
            <TableContainer>
              <Table size="small" data-testid="tool-table">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Tool</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Runs</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Share of usage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.tools.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No tool runs on this stack for this period.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {report.tools.map((row) => (
                    <TableRow key={row.jobType} hover data-testid={`row-${row.jobType}`}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.displayName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                        {row.runs.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(row.percentOfTotal, 100)}
                            sx={{ flex: 1, height: 8, borderRadius: 1 }}
                          />
                          <Typography variant="caption" sx={{ minWidth: 48, textAlign: 'right' }}>
                            {formatPercent(row.percentOfTotal)}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {report.summary.distinctTools.toLocaleString()} tool(s) · generated{' '}
            {new Date(report.generatedAt).toLocaleString()}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default MostUsedToolsPage;
